import React, { useEffect, useRef, useState, useCallback } from "react";

// --- 1. Chart Component ---
const ProfileChart = ({ data, onHover }) => {
  // FIX: Hooks must be called unconditionally at the very top
  const svgRef = useRef(null);
  const [hoverData, setHoverData] = useState(null);

  // FIX: Early return happens AFTER hooks
  if (!data || data.length === 0) {
      return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading Profile...</div>;
  }

  const width = 800, height = 280, padding = 40;
  const maxDist = data[data.length - 1].dist;
  
  // Normalized Y-Axis Logic
  // We normalize so the graph starts at 0 relative height, but keeps the shape
  const minHeight = Math.min(...data.map(d => d.height));
  const maxHeight = Math.max(...data.map(d => d.height));
  
  // Add 10% headroom
  const yMax = maxHeight + ((maxHeight - minHeight) * 0.1) || 10; 
  const yMin = Math.min(0, minHeight); 
  const heightRange = yMax - yMin;

  // Scales
  const getX = (d) => padding + (d / maxDist) * (width - 2 * padding);
  const getY = (h) => height - padding - ((h - yMin) / heightRange) * (height - 2 * padding);

  // Build SVG Path
  let pathD = `M ${getX(data[0].dist)} ${getY(data[0].height)}`;
  data.forEach(p => { pathD += ` L ${getX(p.dist)} ${getY(p.height)}`; });
  const areaD = `${pathD} L ${getX(maxDist)} ${getY(yMin)} L ${getX(0)} ${getY(yMin)} Z`;

  // --- Interaction Logic ---
  const handleMouseMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    
    // Calculate X relative to SVG coordinate system
    const scaleX = width / rect.width; 
    const mouseX = (e.clientX - rect.left) * scaleX;

    // Convert MouseX back to Distance
    let hoverDist = ((mouseX - padding) / (width - 2 * padding)) * maxDist;
    
    // Clamp to bounds
    hoverDist = Math.max(0, Math.min(maxDist, hoverDist));

    // Find closest data point
    const closest = data.reduce((prev, curr) => 
      Math.abs(curr.dist - hoverDist) < Math.abs(prev.dist - hoverDist) ? curr : prev
    );

    setHoverData(closest);
    
    // Tell parent to update Map
    if (onHover) onHover(closest);
  };

  const handleMouseLeave = () => {
    setHoverData(null);
    if (onHover) onHover(null);
  };

  return (
    <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      <h4 style={{ margin: "0 0 5px 0", fontSize: 14, color: '#333' }}>
        Cross Section (Relative Height)
      </h4>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 10 }}>
        Max Height Difference: {maxHeight.toFixed(2)}m | Length: {maxDist.toFixed(1)}m
      </div>
      
      <svg 
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`} 
        style={{ width: '100%', height: 'auto', maxHeight: '300px', overflow: 'visible', cursor: 'crosshair' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Background */}
        <rect x={padding} y={padding} width={width - 2 * padding} height={height - 2 * padding} fill="#f8fafc" />
        
        {/* Axes */}
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#e2e8f0" />
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#e2e8f0" />

        {/* The Graph */}
        <path d={areaD} fill="rgba(37, 99, 235, 0.1)" stroke="none" />
        <path d={pathD} fill="none" stroke="#2563eb" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        
        {/* HOVER INDICATOR */}
        {hoverData && (
            <g>
                {/* Vertical Line */}
                <line 
                    x1={getX(hoverData.dist)} 
                    y1={padding} 
                    x2={getX(hoverData.dist)} 
                    y2={height - padding} 
                    stroke="#ef4444" 
                    strokeWidth="1" 
                    strokeDasharray="4"
                />
                {/* Dot on Line */}
                <circle 
                    cx={getX(hoverData.dist)} 
                    cy={getY(hoverData.height)} 
                    r="4" 
                    fill="#ef4444" 
                    stroke="white" 
                    strokeWidth="2"
                />
                {/* Tooltip Box */}
                <g transform={`translate(${getX(hoverData.dist)}, ${padding + 20})`}>
                    <rect x="-60" y="-25" width="120" height="40" rx="4" fill="rgba(0,0,0,0.8)" />
                    <text x="0" y="-10" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">
                        H: {hoverData.height.toFixed(2)}m
                    </text>
                    <text x="0" y="5" textAnchor="middle" fill="#ccc" fontSize="10">
                        D: {hoverData.dist.toFixed(1)}m
                    </text>
                </g>
            </g>
        )}

        {/* X-Axis Labels */}
        <text x={padding} y={height - 15} fontSize="12" fill="#64748b">0m</text>
        <text x={width - padding} y={height - 15} fontSize="12" fill="#64748b" textAnchor="end">{maxDist.toFixed(1)}m</text>
        
        {/* Y-Axis Labels */}
        <text x={padding - 8} y={getY(yMin)} fontSize="12" fill="#64748b" textAnchor="end">{yMin.toFixed(0)}m</text>
        <text x={padding - 8} y={getY(yMax)} fontSize="12" fill="#64748b" textAnchor="end">{yMax.toFixed(1)}m</text>
      </svg>
    </div>
  );
};

// --- 2. Main Component ---
const CrossSectionCesium = ({ viewer, isActive, onClose, onStatusChange }) => {
  const [profileData, setProfileData] = useState(null);
  const [isProfiling, setIsProfiling] = useState(false);
  const [activePoints, setActivePoints] = useState(null);

  const drawingRef = useRef({ 
    entity: null, 
    lineEntity: null,
    handler: null,
    hoverEntity: null // New: Ref for the red marker on map
  });

  // --- Map Hover Handler (Synchronizes Chart -> Map) ---
  const handleChartHover = (pointData) => {
    if (!viewer || !viewer.entities) return;
    const Cesium = window.Cesium;

    // 1. If no data (mouse left chart), hide the entity
    if (!pointData) {
        if (drawingRef.current.hoverEntity) {
            drawingRef.current.hoverEntity.show = false;
            viewer.scene.requestRender();
        }
        return;
    }

    // 2. Create entity if it doesn't exist yet
    if (!drawingRef.current.hoverEntity) {
        drawingRef.current.hoverEntity = viewer.entities.add({
            point: {
                pixelSize: 15,
                color: Cesium.Color.RED,
                outlineColor: Cesium.Color.WHITE,
                outlineWidth: 2,
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                disableDepthTestDistance: Number.POSITIVE_INFINITY
            },
            label: {
                show: true,
                font: '14px sans-serif',
                fillColor: Cesium.Color.WHITE,
                outlineColor: Cesium.Color.BLACK,
                outlineWidth: 2,
                style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                pixelOffset: new Cesium.Cartesian2(0, -20),
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                disableDepthTestDistance: Number.POSITIVE_INFINITY
            }
        });
    }

    // 3. Update Position & Text on Map
    if (pointData.location) {
        // pointData.location is the Cartographic object we stored in calculateProfile
        const cartesian = Cesium.Cartographic.toCartesian(pointData.location);
        
        drawingRef.current.hoverEntity.position = cartesian;
        drawingRef.current.hoverEntity.label.text = `${pointData.height.toFixed(1)}m`;
        drawingRef.current.hoverEntity.show = true;
        
        viewer.scene.requestRender();
    }
  };

  // --- A. CALCULATION LOGIC ---
  const calculateProfile = useCallback(async (startCartesian, endCartesian) => {
    if (!viewer) return;
    const Cesium = window.Cesium;
    
    setIsProfiling(true);
    if (onStatusChange) onStatusChange("Sampling terrain...");

    try {
      const startCart = Cesium.Cartographic.fromCartesian(startCartesian);
      const endCart = Cesium.Cartographic.fromCartesian(endCartesian);
      
      const count = 100;
      const positions = [];
      for (let i = 0; i < count; i++) {
        const t = i / (count - 1);
        const lon = Cesium.Math.lerp(startCart.longitude, endCart.longitude, t);
        const lat = Cesium.Math.lerp(startCart.latitude, endCart.latitude, t);
        positions.push(new Cesium.Cartographic(lon, lat, 0));
      }

      let updatedPositions = [];
      
      // 1. Try Terrain Provider (Best for ground)
      if (viewer.terrainProvider && !(viewer.terrainProvider instanceof Cesium.EllipsoidTerrainProvider)) {
          try {
              updatedPositions = await Cesium.sampleTerrainMostDetailed(viewer.terrainProvider, positions);
          } catch(e) { console.warn("Terrain fail", e); }
      }
      
      // 2. Fallback to Scene (Best for 3D Tiles)
      if (!updatedPositions.length || updatedPositions.length < count) {
          try {
              updatedPositions = await viewer.scene.sampleHeightMostDetailed(positions);
          } catch (e2) { updatedPositions = positions; }
      }

      // Calculate Normalization Base (Min Height)
      const rawHeights = updatedPositions.map(p => p.height || 0);
      const minAbsoluteHeight = Math.min(...rawHeights);

      let totalDist = 0;
      const chartData = updatedPositions.map((pos, i) => {
        if (i > 0) {
          const prev = updatedPositions[i - 1];
          const geodesic = new Cesium.EllipsoidGeodesic(prev, pos);
          totalDist += geodesic.surfaceDistance;
        }
        
        const absoluteH = pos.height || 0;
        const relativeH = absoluteH - minAbsoluteHeight;

        return { 
          dist: totalDist, 
          height: relativeH, 
          absHeight: absoluteH,
          location: pos // <--- IMPORTANT: Storing Cartographic here for the Map Marker
        };
      });

      setProfileData(chartData);
      if (onStatusChange) onStatusChange("Profile Updated.");

    } catch (err) {
      console.error("Profile Error:", err);
      if (onStatusChange) onStatusChange("Error calculating profile");
    } finally {
      setIsProfiling(false);
    }
  }, [viewer, onStatusChange]);


  // --- B. LISTENER (Updates graph if map changes/zooms) ---
  useEffect(() => {
      if (!viewer || !viewer.scene || !isActive || !activePoints || !activePoints.end) return;

      const handleTerrainChange = () => {
          // Debounce slightly to avoid thrashing
          setTimeout(() => {
              calculateProfile(activePoints.start, activePoints.end);
          }, 1000);
      };

      const eventHelper = viewer.scene.terrainProviderChanged;
      eventHelper.addEventListener(handleTerrainChange);

      return () => {
          eventHelper.removeEventListener(handleTerrainChange);
      };
  }, [viewer, isActive, activePoints, calculateProfile]);


  // --- C. DRAWING LOGIC ---
  useEffect(() => {
    if (!viewer || !isActive) return;
    const Cesium = window.Cesium;
    viewer.scene.pickTranslucentDepth = true;

    if (!drawingRef.current.handler) {
      drawingRef.current.handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    }
    const handler = drawingRef.current.handler;

    if (onStatusChange) onStatusChange("Click Start Point");

    handler.setInputAction((movement) => {
      let cartesian = viewer.scene.pickPosition(movement.position);
      if (!cartesian) {
         const ray = viewer.camera.getPickRay(movement.position);
         cartesian = viewer.scene.globe.pick(ray, viewer.scene);
      }
      if (!cartesian) return;

      if (!activePoints || (activePoints.start && activePoints.end)) {
        // Reset Logic
        if (drawingRef.current.entity) viewer.entities.remove(drawingRef.current.entity);
        if (drawingRef.current.lineEntity) viewer.entities.remove(drawingRef.current.lineEntity);
        if (drawingRef.current.hoverEntity) {
             viewer.entities.remove(drawingRef.current.hoverEntity);
             drawingRef.current.hoverEntity = null;
        }
        setProfileData(null);
        
        setActivePoints({ start: cartesian, end: null });

        // Draw Start Dot
        drawingRef.current.entity = viewer.entities.add({
          position: cartesian,
          point: { 
            pixelSize: 12, color: Cesium.Color.YELLOW, outlineColor: Cesium.Color.BLACK, outlineWidth: 2,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND, disableDepthTestDistance: Number.POSITIVE_INFINITY 
          }
        });
        if (onStatusChange) onStatusChange("Click End Point");
      } 
      else {
        // End Point Logic
        const newPoints = { ...activePoints, end: cartesian };
        setActivePoints(newPoints);

        if (drawingRef.current.entity) viewer.entities.remove(drawingRef.current.entity);
        drawingRef.current.entity = null;

        // Draw Line
        drawingRef.current.lineEntity = viewer.entities.add({
          polyline: {
            positions: [newPoints.start, newPoints.end],
            width: 5,
            material: new Cesium.PolylineOutlineMaterialProperty({
                color: Cesium.Color.YELLOW, outlineWidth: 2, outlineColor: Cesium.Color.BLACK
            }),
            clampToGround: true,
            classificationType: Cesium.ClassificationType.TERRAIN
          }
        });

        calculateProfile(newPoints.start, newPoints.end);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    return () => {
      if (drawingRef.current.handler) {
        drawingRef.current.handler.destroy();
        drawingRef.current.handler = null;
      }
    };
  }, [viewer, isActive, activePoints, calculateProfile, onStatusChange]);

  // Cleanup on Close/Unmount
  useEffect(() => {
      if(!isActive && viewer) {
          if (drawingRef.current.entity) viewer.entities.remove(drawingRef.current.entity);
          if (drawingRef.current.lineEntity) viewer.entities.remove(drawingRef.current.lineEntity);
          if (drawingRef.current.hoverEntity) viewer.entities.remove(drawingRef.current.hoverEntity);
          setProfileData(null);
          setActivePoints(null);
      }
  }, [isActive, viewer]);

  if (!isActive) return null;

  return (
    <div style={bottomPanelStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
        <div style={{display:'flex', alignItems:'center', gap: 10}}>
          <strong style={{ color: '#333', fontSize: 14 }}>Cross Section Analysis</strong>
          {isProfiling && <span style={{ color: '#2563eb', fontWeight: 'bold', fontSize: 12 }}> Processing...</span>}
        </div>
        <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, fontWeight:'bold' }}>✕</button>
      </div>

      {profileData ? (
        <ProfileChart 
            data={profileData} 
            onHover={handleChartHover} // Pass the handler
        />
      ) : (
        <div style={{ color: '#666', padding: '40px', textAlign: 'center', border: '2px dashed #ccc', borderRadius: 8, background: '#f9f9f9' }}>
          <p style={{ margin: 0, fontWeight: 500 }}>Click <strong>Start</strong> and <strong>End</strong> points on map.</p>
        </div>
      )}
    </div>
  );
};

const bottomPanelStyle = {
  position: "absolute", bottom: 30, left: '40%', transform: 'translateX(-50%)',
  zIndex: 2002, background: "rgba(255,255,255,0.98)", padding: 10, borderRadius: 12,
  boxShadow: "0 8px 24px rgba(0,0,0,0.3)", width: "90vw", maxWidth: "1000px",
  minHeight: "200px", fontSize: 13, fontFamily: "sans-serif"
};

export default CrossSectionCesium;